import { APIClient } from "../helpers/api_helper";
import { ApiResponse, PaginationMeta } from "../types/api";

const api = new APIClient();

export type CrmPurpose = "sales" | "procurement" | "custom";
export type CrmSource = "whatsapp" | "landing" | "manual";

export interface CrmPipelineStage {
  id: number;
  pipeline_id: number;
  name: string;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

export interface CrmPipeline {
  id: number;
  name: string;
  purpose: CrmPurpose;
  template_slug: string | null;
  sort_order: number;
  archived_at: string | null;
  stages?: CrmPipelineStage[];
}

export interface CrmPipelineTemplate {
  id: number;
  slug: string;
  name: string;
  purpose: CrmPurpose;
  default_stages: Array<{
    name: string;
    sort_order: number;
    is_won: boolean;
    is_lost: boolean;
  }>;
}

export interface CrmDeal {
  id: number;
  pipeline_id: number;
  stage_id: number;
  client_id: number | null;
  counterparty_label: string | null;
  source: CrmSource;
  title: string;
  estimated_value: number | null;
  assigned_user_id: number | null;
  due_at: string | null;
  won_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  notes?: string | null;
  quotes_count?: number;
  latest_quote_status?: string | null;
  latest_quote_total_amount?: number | null;
  latest_quote_public_url?: string | null;
  stage?: CrmPipelineStage;
  client?: { id: number; name: string } | null;
  assigned_user?: { id: number; name: string } | null;
}

export interface CreateCrmPipelinePayload {
  name?: string;
  purpose?: CrmPurpose;
  template_slug?: string;
}

export interface CreateCrmDealPayload {
  title: string;
  stage_id?: number;
  client_id?: number | null;
  counterparty_label?: string | null;
  source?: CrmSource;
  estimated_value?: number | null;
  assigned_user_id?: number | null;
  due_at?: string | null;
}

export interface UpdateCrmDealPayload {
  title?: string;
  stage_id?: number;
  client_id?: number | null;
  counterparty_label?: string | null;
  source?: CrmSource;
  estimated_value?: number | null;
  assigned_user_id?: number | null;
  due_at?: string | null;
  lost_reason?: string | null;
  notes?: string | null;
}

export const getCrmPipelineTemplates = async (): Promise<CrmPipelineTemplate[]> => {
  const response = await api.get("/crm/pipeline-templates") as ApiResponse<CrmPipelineTemplate[]>;
  return response.data ?? [];
};

export const getCrmPipelines = async (): Promise<CrmPipeline[]> => {
  const response = await api.get("/crm/pipelines") as ApiResponse<CrmPipeline[]>;
  return response.data ?? [];
};

export const createCrmPipeline = async (
  payload: CreateCrmPipelinePayload
): Promise<CrmPipeline> => {
  const response = await api.create("/crm/pipelines", payload) as ApiResponse<CrmPipeline>;
  return response.data;
};

export const getCrmPipeline = async (pipelineId: number): Promise<CrmPipeline> => {
  const response = await api.get(`/crm/pipelines/${pipelineId}`) as ApiResponse<CrmPipeline>;
  return response.data;
};

export const getCrmDeals = async (
  pipelineId: number,
  params?: {
    overdue?: boolean;
    stage_id?: number;
    search?: string;
    assigned_user_id?: number;
  }
): Promise<{ data: CrmDeal[]; meta?: PaginationMeta }> => {
  const query = new URLSearchParams();
  if (params?.overdue) {
    query.set("overdue", "1");
  }
  if (params?.stage_id) {
    query.set("stage_id", String(params.stage_id));
  }
  if (params?.search?.trim()) {
    query.set("search", params.search.trim());
  }
  if (params?.assigned_user_id) {
    query.set("assigned_user_id", String(params.assigned_user_id));
  }
  query.set("per_page", "100");

  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await api.get(
    `/crm/pipelines/${pipelineId}/deals${suffix}`
  ) as ApiResponse<CrmDeal[]> & { meta?: PaginationMeta };

  return { data: response.data ?? [], meta: response.meta };
};

export const createCrmDeal = async (
  pipelineId: number,
  payload: CreateCrmDealPayload
): Promise<CrmDeal> => {
  const response = await api.create(
    `/crm/pipelines/${pipelineId}/deals`,
    payload
  ) as ApiResponse<CrmDeal>;
  return response.data;
};

export const updateCrmDeal = async (
  dealId: number,
  payload: UpdateCrmDealPayload
): Promise<CrmDeal> => {
  const response = await api.update(`/crm/deals/${dealId}`, payload) as ApiResponse<CrmDeal>;
  return response.data;
};

export const duplicateCrmDeal = async (
  dealId: number,
  includeLatestQuote = true
): Promise<{ deal: CrmDeal; quote: CrmQuote | null }> => {
  const response = await api.create(`/crm/deals/${dealId}/duplicate`, {
    include_latest_quote: includeLatestQuote,
  }) as ApiResponse<{ deal: CrmDeal; quote: CrmQuote | null }>;
  return response.data;
};

export interface CrmQuoteItem {
  id?: number;
  item_type: "service" | "package";
  service_id?: number | null;
  service_package_id?: number | null;
  quantity: number;
  unit_price: number;
  line_total?: number;
  service?: { id: number; name: string } | null;
  service_package?: { id: number; name: string } | null;
}

export interface CrmQuote {
  id: number;
  deal_id: number;
  client_id: number;
  order_id: number | null;
  total_amount: number;
  valid_until: string | null;
  status: string;
  public_token?: string | null;
  public_url?: string | null;
  items?: CrmQuoteItem[];
  studio?: { id: number; name: string } | null;
}

export interface CreateCrmQuotePayload {
  client_id?: number;
  valid_until?: string | null;
  items: Array<{
    item_type: "service" | "package";
    service_id?: number;
    service_package_id?: number;
    quantity?: number;
    unit_price: number;
  }>;
}

export interface SendCrmQuoteResult {
  quote: CrmQuote;
  public_url: string;
  copy_text: string;
  email_queued: boolean;
}

export interface AcceptCrmQuoteResult {
  quote: CrmQuote;
  order_id: number;
  client_id?: number | null;
  agenda_cta?: boolean;
}

export const getCrmQuotes = async (dealId: number): Promise<CrmQuote[]> => {
  const response = await api.get(`/crm/deals/${dealId}/quotes`) as ApiResponse<CrmQuote[]>;
  return response.data ?? [];
};

export const createCrmQuote = async (
  dealId: number,
  payload: CreateCrmQuotePayload
): Promise<CrmQuote> => {
  const response = await api.create(
    `/crm/deals/${dealId}/quotes`,
    payload
  ) as ApiResponse<CrmQuote>;
  return response.data;
};

export const cloneCrmQuote = async (quoteId: number): Promise<CrmQuote> => {
  const response = await api.create(
    `/crm/quotes/${quoteId}/clone`,
    {}
  ) as ApiResponse<CrmQuote>;
  return response.data;
};

export const sendCrmQuote = async (
  quoteId: number,
  sendEmail = true
): Promise<SendCrmQuoteResult> => {
  const response = await api.create(`/crm/quotes/${quoteId}/send`, {
    send_email: sendEmail,
  }) as ApiResponse<SendCrmQuoteResult>;
  return response.data;
};

export const acceptCrmQuote = async (
  quoteId: number,
  unitId?: number
): Promise<AcceptCrmQuoteResult> => {
  const response = await api.create(`/crm/quotes/${quoteId}/accept`, {
    unit_id: unitId,
  }) as ApiResponse<AcceptCrmQuoteResult>;
  return response.data;
};
